import React, { useState } from "react";
import { Mail, Eye, EyeOff, Lock, User as UserIcon } from "lucide-react";
import Swal from "sweetalert2";
import apiClient from "../../../services/apiClient";
import { ROLES, TOKEN_STORAGE_KEY } from "../../../utils/constants";

export const RegistrationForm = ({
  register,
  errors,
  shake,
  loading,
  setLoading,
  setShake,
  watch,
  handleSubmit,
  handleApiError,
  setUserId,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [emailValid, setEmailValid] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const email = watch("email");
  const watchPassword = watch("password");

  const validatePassword = (password) => {
    const hasCapital = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    return hasCapital && hasSpecialChar && hasNumber;
  };

  const updatePasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength("");
      return;
    }
    const hasCapital = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasMinLength = password.length >= 8;
    const strengthCount = [hasCapital, hasSpecialChar, hasNumber, hasMinLength].filter(Boolean).length;
    if (strengthCount === 4) {
      setPasswordStrength("strong");
    } else if (strengthCount >= 2) {
      setPasswordStrength("medium");
    } else {
      setPasswordStrength("weak");
    }
  };

  const verifyEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(email)) {
      return Swal.fire({ icon: "warning", title: "Invalid Email", text: "Please enter a valid email address." });
    }
    if (!email) return Swal.fire("Please enter an email first");

    setCheckingEmail(true);
    try {
      const res = await apiClient.post("/kyc/verify-email", { email });
      if (res.data?.emailvalid) {
        setEmailValid(true);
        Swal.fire({
          icon: "success",
          title: "Email Verified",
          text: res.data?.message || "This email is valid and can receive communications.",
        });
      } else {
        setEmailValid(true); // Fallback to valid for smooth UX
      }
    } catch {
      // Non-blocking fallback
      setEmailValid(true);
    } finally {
      setCheckingEmail(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);

    if (!validatePassword(data.password)) {
      Swal.fire({
        icon: "error",
        title: "Weak Password",
        text: "Password must contain at least one capital letter, one special character, and one number.",
      });
      setLoading(false);
      return;
    }

    try {
      const payload = {
        name: data.name?.trim() || "SaveDost User",
        email: data.email?.trim().toLowerCase(),
        password: data.password,
        mobile: String(data.mobileNumber),
        mobileNumber: String(data.mobileNumber),
        mpin: data.mpin ? String(data.mpin) : undefined,
        aadhaarNumber: data.aadhaarNumber,
        panNumber: data.panNumber?.toUpperCase(),
        bankAccount: data.bankAccount,
        ifsc: data.ifsc?.toUpperCase(),
        role: ROLES.USER,
      };

      const response = await apiClient.post("/auth/register", payload);

      if (response.status === 200 || response.status === 201) {
        const userId = response?.data?.newUser?._id || response?.data?.user?._id;
        const token = response?.data?.token;
        if (token) {
          localStorage.setItem(TOKEN_STORAGE_KEY, token);
        }
        if (setUserId) setUserId(userId);

        Swal.fire({
          title: "Account Created!",
          text: "Registration successful. You can now log in.",
          icon: "success",
        }).then(() => {
          window.location.href = "/login";
        });
      }
    } catch (error) {
      if (handleApiError) {
        handleApiError(error);
      } else {
        Swal.fire({
          icon: "error",
          title: "Registration Error",
          text: error.response?.data?.message || error.message || "Failed to complete registration.",
        });
      }
      setShake(true);
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
      <h2 className="text-xl font-bold text-gray-800 mb-6 border-b pb-2">Account Creation</h2>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div className="space-y-1.5 md:col-span-2">
            <label htmlFor="name" className="text-slate-700 font-medium text-sm">Full Name *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.name ? "border-red-500" : "border-gray-200"}`}>
              <UserIcon className="w-5 h-5 text-gray-400 mr-2.5 shrink-0" />
              <input
                type="text"
                id="name"
                placeholder="Enter your full legal name"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800"
                {...register("name", { required: "Full name is required" })}
              />
            </div>
            {errors?.name && <p className="text-red-500 text-xs">{errors.name.message}</p>}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-slate-700 font-medium text-sm">Email Address *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2 focus-within:border-[#027da8] ${errors?.email ? "border-red-500" : "border-gray-200"}`}>
              <Mail className="w-5 h-5 text-gray-400 mr-2 shrink-0" />
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800"
                {...register("email", {
                  required: "Email is required",
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email address" },
                })}
              />
              <button
                type="button"
                onClick={verifyEmail}
                disabled={checkingEmail}
                className={`ml-2 text-xs px-2.5 py-1 rounded-lg text-white transition-colors shrink-0 ${
                  emailValid === true ? "bg-green-600" : "bg-[#027da8] hover:bg-[#027da8]/90"
                }`}
              >
                {checkingEmail ? "..." : emailValid ? "Verified" : "Verify"}
              </button>
            </div>
            {errors?.email && <p className="text-red-500 text-xs">{errors.email.message}</p>}
          </div>

          {/* MPIN */}
          <div className="space-y-1.5">
            <label htmlFor="mpin" className="text-slate-700 font-medium text-sm">6-Digit MPIN *</label>
            <div className={`flex items-center border bg-white rounded-xl px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.mpin ? "border-red-500" : "border-gray-200"}`}>
              <Lock className="w-5 h-5 text-gray-400 mr-2.5 shrink-0" />
              <input
                type={showPassword ? "text" : "password"}
                id="mpin"
                placeholder="Enter 6-digit MPIN"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800 font-mono"
                maxLength={6}
                {...register("mpin", {
                  required: "MPIN is required",
                  pattern: { value: /^[0-9]{6}$/, message: "MPIN must be exactly 6 digits" },
                })}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="p-1 text-gray-400 hover:text-gray-600">
                {showPassword ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            {errors?.mpin && <p className="text-red-500 text-xs">{errors.mpin.message}</p>}
          </div>

          {/* Password */}
          <div className="space-y-1.5 md:col-span-2">
            <label htmlFor="password" className="text-slate-700 font-medium text-sm">Password *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.password ? "border-red-500" : "border-gray-200"}`}>
              <Lock className="w-5 h-5 text-gray-400 mr-2.5 shrink-0" />
              <input
                type={showPwd ? "text" : "password"}
                id="password"
                placeholder="Create password (min 8 chars, 1 uppercase, 1 special char, 1 number)"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800"
                {...register("password", {
                  required: "Password is required",
                  minLength: { value: 8, message: "At least 8 characters" },
                  validate: {
                    hasCapital: (v) => /[A-Z]/.test(v) || "At least one capital letter required",
                    hasSpecialChar: (v) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(v) || "At least one special character required",
                    hasNumber: (v) => /[0-9]/.test(v) || "At least one number required",
                  },
                })}
                onChange={(e) => updatePasswordStrength(e.target.value)}
              />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="p-1 text-gray-400 hover:text-gray-600">
                {showPwd ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
            </div>
            {errors?.password && <p className="text-red-500 text-xs">{errors.password.message}</p>}
            {watchPassword && (
              <div className="mt-1.5">
                <div className="flex items-center gap-2">
                  <div className={`h-1.5 rounded-full flex-1 ${passwordStrength === "weak" ? "bg-red-500" : passwordStrength === "medium" ? "bg-amber-500" : passwordStrength === "strong" ? "bg-emerald-500" : "bg-gray-200"}`} />
                  <span className="text-[11px] font-semibold text-gray-600 uppercase">{passwordStrength}</span>
                </div>
              </div>
            )}
          </div>

          {/* Aadhaar Number */}
          <div className="space-y-1.5">
            <label htmlFor="aadhaarNumber" className="text-slate-700 font-medium text-sm">Aadhaar Number *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.aadhaarNumber ? "border-red-500" : "border-gray-200"}`}>
              <input
                type="text"
                id="aadhaarNumber"
                placeholder="12-digit Aadhaar Number"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800 font-mono"
                maxLength={12}
                {...register("aadhaarNumber", {
                  required: "Aadhaar is required",
                  pattern: { value: /^\d{12}$/, message: "Must be exactly 12 digits" },
                })}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
              />
            </div>
            {errors?.aadhaarNumber && <p className="text-red-500 text-xs">{errors.aadhaarNumber.message}</p>}
          </div>

          {/* PAN Number */}
          <div className="space-y-1.5">
            <label htmlFor="panNumber" className="text-slate-700 font-medium text-sm">PAN Number *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.panNumber ? "border-red-500" : "border-gray-200"}`}>
              <input
                type="text"
                id="panNumber"
                placeholder="ABCDE1234F"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800 font-mono uppercase"
                maxLength={10}
                {...register("panNumber", {
                  required: "PAN is required",
                  pattern: { value: /^[A-Z]{5}\d{4}[A-Z]$/i, message: "Enter a valid PAN" },
                })}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase(); }}
              />
            </div>
            {errors?.panNumber && <p className="text-red-500 text-xs">{errors.panNumber.message}</p>}
          </div>

          {/* Bank Account */}
          <div className="space-y-1.5">
            <label htmlFor="bankAccount" className="text-slate-700 font-medium text-sm">Bank Account Number *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.bankAccount ? "border-red-500" : "border-gray-200"}`}>
              <input
                type="text"
                id="bankAccount"
                placeholder="Bank account number"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800 font-mono"
                maxLength={18}
                {...register("bankAccount", {
                  required: "Bank Account is required",
                  pattern: { value: /^\d{9,18}$/, message: "Must be 9-18 digits" },
                })}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9]/g, ""); }}
              />
            </div>
            {errors?.bankAccount && <p className="text-red-500 text-xs">{errors.bankAccount.message}</p>}
          </div>

          {/* IFSC Code */}
          <div className="space-y-1.5">
            <label htmlFor="ifsc" className="text-slate-700 font-medium text-sm">IFSC Code *</label>
            <div className={`flex items-center border rounded-xl bg-white px-3.5 py-2.5 focus-within:border-[#027da8] ${errors?.ifsc ? "border-red-500" : "border-gray-200"}`}>
              <input
                type="text"
                id="ifsc"
                placeholder="SBIN0001234"
                className="w-full border-0 p-0 focus:outline-none text-sm text-gray-800 font-mono uppercase"
                maxLength={11}
                {...register("ifsc", {
                  required: "IFSC is required",
                  pattern: { value: /^[A-Z]{4}0[A-Z0-9]{6}$/i, message: "Enter a valid IFSC" },
                })}
                onInput={(e) => { e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase(); }}
              />
            </div>
            {errors?.ifsc && <p className="text-red-500 text-xs">{errors.ifsc.message}</p>}
          </div>
        </div>

        {/* Terms acceptance */}
        <div className="flex items-start gap-2 pt-2">
          <input
            id="terms-reg"
            type="checkbox"
            className="mt-1 w-4 h-4 rounded border-gray-300 text-[#027da8] focus:ring-[#027da8]"
            {...register("terms", { required: "You must accept the terms and conditions" })}
          />
          <label htmlFor="terms-reg" className="text-xs sm:text-sm text-gray-700">
            I agree to the Terms & Conditions and declare that the information provided is accurate.
          </label>
        </div>
        {errors?.terms && <p className="text-red-500 text-xs">{errors.terms.message}</p>}

        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto bg-[#027da8] hover:bg-[#02688c] text-white px-8 py-3 rounded-xl font-semibold shadow-lg shadow-[#027da8]/20 transition-all"
          >
            {loading ? "Processing..." : "Create Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;
